/**
 * Company lifecycle calls (R3-W1) on the V2 employment service, next to the
 * employer CRUD in `employers.ts`:
 *
 *   - the headcount counters, read to lock the employee-ID format once the
 *     company has employees (decision D11);
 *   - the admin purge (decision D3): a read-only preview, then the bounded-pass
 *     `DELETE ?purge=true&confirm=<id>` repeated until `Completed: true`;
 *   - the presigned logo flow (decision D2): upload-url → PUT to S3 → adopt;
 *     read; remove;
 *   - the company-tree helpers behind the parent Select (V1
 *     `computeParentOptions`).
 *
 * Every URL is written out in full at its call site so
 * `scripts/openapi-sync-check.ts` can assert it against the contract.
 * Writes skip the global error dialog: each surface renders the server's
 * RFC 9457 `detail` inline.
 */
import { httpClient } from '@/api/httpClient';
import { warnOnce } from '@/api/warnOnce';
import { employerFullName, type Employer } from '@/api/services/employers';

/** Writes (and reads whose refusal is shown inline) skip the global dialog. */
const INLINE_ERRORS = { skipGlobalError: true } as const;

/**
 * Path-encode one identifier.
 * @param value the identifier to encode.
 * @returns the encoded segment.
 */
function seg(value: string): string {
  return encodeURIComponent(value);
}

/**
 * A finite number or zero.
 * @param v any wire value.
 * @returns the number, or 0.
 */
function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

// ---------------------------------------------------------------------------
// Counters (decision D11)
// ---------------------------------------------------------------------------

/** The subset of `Counters` the profile reads. */
export interface EmployerCounters {
  /** Every employment record of the employer, whatever its status. */
  all: number;
  active: number;
}

/**
 * One employer's headcount counters. An employer whose counters were never
 * written answers zeros (not a 404).
 * @param employerId the employer's full id.
 * @returns the counters the profile needs.
 * @throws when `employerId` is empty, or on an HTTP failure.
 */
export async function getEmployerCounters(employerId: string): Promise<EmployerCounters> {
  if (!employerId) throw new Error('employerId is required');
  const { data } = await httpClient.get<{ All?: unknown; Active?: unknown }>(
    `/employment/v1/employers/${seg(employerId)}/counters`,
  );
  return { all: num(data?.All), active: num(data?.Active) };
}

// ---------------------------------------------------------------------------
// Purge (decision D3) — preview + bounded passes
// ---------------------------------------------------------------------------

export type PurgeBlockerCode = 'TENANT_EMPLOYER' | 'HAS_CHILD_EMPLOYERS' | 'UNKNOWN';

export interface PurgeBlocker {
  code: PurgeBlockerCode;
  /** The raw code, kept so an unknown one can still be shown verbatim. */
  rawCode: string;
  /** The server's sentence (what the DELETE would refuse with). */
  detail: string;
}

export interface PurgePreviewFamily {
  family: string;
  count: number;
}

export interface PurgePreview {
  employerId: string;
  companyName: string;
  purgeable: boolean;
  blockers: PurgeBlocker[];
  /** Families with a non-zero count, largest first. */
  families: PurgePreviewFamily[];
  totalItems: number;
  hasLogo: boolean;
}

/** `EmployerPurgePreview` as the wire answers it. */
export interface PurgePreviewWire {
  EmployerId?: string;
  CompanyName?: string;
  Purgeable?: boolean;
  Blockers?: { Code?: string; Detail?: string }[];
  ByFamily?: Record<string, unknown>;
  TotalItems?: number;
  HasLogo?: boolean;
}

/**
 * The item families the purge preview is known to name (the platform's
 * `purgeFamilies`). A family outside this list is still shown, under its raw
 * name, and reported once via `warnOnce` so a new backend family surfaces in
 * monitoring instead of silently vanishing.
 */
export const KNOWN_PURGE_FAMILIES = [
  'Employer',
  'Employee',
  'EmployeeCode',
  'Person',
  'FamilyMember',
  'PersonDocument',
  'BankDetail',
  'EmployeeDocument',
  'Insurance',
  'EmployeeSetting',
  'Contract',
  'ContractEvent',
  'EmploymentChange',
  'EmploymentAction',
  'CompensationEvent',
  'Position',
  'OrgUnit',
  'JobProfile',
  'JobGrade',
  'JobDescription',
  'BaseLocation',
  'WorkZoneBatch',
  'Oversight',
  'CostCenter',
  'CostCenterCode',
  'EmployeeBatch',
  'Event',
  'ActionRegistry',
] as const;

/**
 * Normalise the purge preview. Zero-count families are dropped and the rest
 * sorted by count, largest first (ties by name). `purgeable` is `true` only
 * when the server says so AND no blocker is present — the contract says the
 * two agree, and the safe reading of a disagreement is "not purgeable".
 * @param wire the preview body.
 * @returns the FE preview.
 */
export function normalizePurgePreview(wire: PurgePreviewWire): PurgePreview {
  const blockers: PurgeBlocker[] = (wire.Blockers ?? []).map((b) => {
    const raw = b.Code?.trim() ?? '';
    let code: PurgeBlockerCode;
    if (raw === 'TENANT_EMPLOYER' || raw === 'HAS_CHILD_EMPLOYERS') {
      code = raw;
    } else {
      warnOnce(`Unknown purge blocker: ${raw}`);
      code = 'UNKNOWN';
    }
    return { code, rawCode: raw, detail: b.Detail?.trim() ?? '' };
  });
  const known = new Set<string>(KNOWN_PURGE_FAMILIES);
  const families: PurgePreviewFamily[] = [];
  for (const [family, value] of Object.entries(wire.ByFamily ?? {})) {
    const count = num(value);
    if (count <= 0) continue;
    if (!known.has(family)) warnOnce(`Unknown purge family: ${family}`);
    families.push({ family, count });
  }
  families.sort((a, b) => b.count - a.count || a.family.localeCompare(b.family));
  const summed = families.reduce((n, f) => n + f.count, 0);
  return {
    employerId: wire.EmployerId ?? '',
    companyName: wire.CompanyName?.trim() ?? '',
    purgeable: wire.Purgeable === true && blockers.length === 0,
    blockers,
    families,
    totalItems:
      typeof wire.TotalItems === 'number' && Number.isFinite(wire.TotalItems)
        ? wire.TotalItems
        : summed,
    hasLogo: wire.HasLogo === true,
  };
}

/**
 * What a purge would remove, and whether anything forbids it. Tenant-admin
 * only (403 otherwise). Read-only.
 * @param employerId the company's full id.
 * @returns the normalised preview.
 * @throws when `employerId` is empty, or on an HTTP failure.
 */
export async function getPurgePreview(employerId: string): Promise<PurgePreview> {
  if (!employerId) throw new Error('employerId is required');
  const { data } = await httpClient.get<PurgePreviewWire>(
    `/employment/v1/employers/${seg(employerId)}/purge-preview`,
    INLINE_ERRORS,
  );
  return normalizePurgePreview(data ?? {});
}

/** One purge pass's answer (`EmployerPurgeSummary`). */
export interface PurgePassResult {
  employerId: string;
  reason: string;
  deletedItems: number;
  completed: boolean;
  remaining: number;
}

/**
 * The confirm text the user typed, as it is compared: surrounding whitespace
 * trimmed, nothing else (case and inner characters must match exactly).
 * @param typed the raw input.
 * @returns the comparable value.
 */
export function normalizeConfirm(typed: string): string {
  return typed.trim();
}

/**
 * Run ONE pass of the admin purge:
 * `DELETE /employment/v1/employers/{e}?purge=true&confirm=<e>`. The caller
 * repeats it until `completed` (see `runEmployerPurge`). No Idempotency-Key is
 * sent: every pass must really run, and a replay would answer the first pass.
 * @param employerId the company's full id.
 * @param confirm the id the user typed back; must equal `employerId` exactly.
 * @returns the pass summary.
 * @throws when `confirm` does not match (nothing is sent), or the server refuses.
 */
export async function purgeEmployerPass(
  employerId: string,
  confirm: string,
): Promise<PurgePassResult> {
  if (!employerId) throw new Error('employerId is required');
  if (confirm !== employerId) throw new Error('confirm must match employerId');
  const { data } = await httpClient.delete<{
    EmployerId?: string;
    Reason?: string;
    DeletedItems?: number;
    Completed?: boolean;
    Remaining?: number;
  }>(`/employment/v1/employers/${seg(employerId)}`, {
    ...INLINE_ERRORS,
    params: { purge: 'true', confirm },
  });
  return {
    employerId: data?.EmployerId ?? employerId,
    reason: data?.Reason ?? '',
    deletedItems: num(data?.DeletedItems),
    // `Completed` is required by the contract; only an explicit `false` means
    // "send it again" (a pre-R3 single-pass server always finished).
    completed: data?.Completed !== false,
    remaining: num(data?.Remaining),
  };
}

/** Why `runEmployerPurge` stopped short of `Completed`. */
export class PurgeStalledError extends Error {
  /**
   * @param reason `no-progress` (a pass left `Remaining` where it was) or
   *   `too-many-passes` (the pass cap was reached).
   * @param remaining the last `Remaining` the server reported.
   */
  constructor(
    readonly reason: 'no-progress' | 'too-many-passes',
    readonly remaining: number,
  ) {
    super(`Purge stopped: ${reason} (${remaining} remaining)`);
    this.name = 'PurgeStalledError';
  }
}

/** The default cap on purge passes (each pass is ~20 s of server work). */
export const MAX_PURGE_PASSES = 30;

/**
 * Repeat the purge until the server answers `Completed: true`. Throws
 * `PurgeStalledError` when a pass does not strictly reduce `Remaining`, or
 * after `maxPasses` passes, so a server that stops making progress can never
 * loop the browser forever.
 * @param employerId the company's full id.
 * @param confirm the id typed back.
 * @param opts `onProgress` after every pass; `maxPasses` (default 30); `pass`
 *   to inject the single-pass call (tests).
 * @returns the final (completed) pass.
 * @throws `PurgeStalledError`, or whatever a pass throws (server refusal).
 */
export async function runEmployerPurge(
  employerId: string,
  confirm: string,
  opts: {
    onProgress?: (result: PurgePassResult, pass: number) => void;
    maxPasses?: number;
    pass?: typeof purgeEmployerPass;
  } = {},
): Promise<PurgePassResult> {
  const maxPasses = opts.maxPasses ?? MAX_PURGE_PASSES;
  const runPass = opts.pass ?? purgeEmployerPass;
  let previous = Number.POSITIVE_INFINITY;
  for (let pass = 1; pass <= maxPasses; pass += 1) {
    const result = await runPass(employerId, confirm);
    opts.onProgress?.(result, pass);
    if (result.completed) return result;
    if (result.remaining >= previous) {
      throw new PurgeStalledError('no-progress', result.remaining);
    }
    previous = result.remaining;
  }
  throw new PurgeStalledError('too-many-passes', Number.isFinite(previous) ? previous : 0);
}

// ---------------------------------------------------------------------------
// Logo (decision D2) — presigned upload, adopt, read, remove
// ---------------------------------------------------------------------------

/** The types the logo accepts — client check; the server re-checks the bytes. */
export const LOGO_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
/** 2 MB, the server's `MaxBytes`. */
export const LOGO_MAX_BYTES = 2 * 1024 * 1024;

export interface EmployerLogo {
  url: string;
  expiresIn: number;
  contentType: string;
  updatedAt: string;
}

interface EmployerLogoWire {
  EmployerId?: string;
  Url?: string;
  ExpiresIn?: number;
  ContentType?: string;
  UpdatedAt?: string;
}

/**
 * Normalise `EmployerLogo`.
 * @param wire the logo body.
 * @returns the FE logo.
 */
function normalizeLogo(wire: EmployerLogoWire | undefined): EmployerLogo {
  return {
    url: wire?.Url ?? '',
    expiresIn: num(wire?.ExpiresIn),
    contentType: wire?.ContentType ?? '',
    updatedAt: wire?.UpdatedAt ?? '',
  };
}

/**
 * Which client-side rule a logo file breaks, if any.
 * @param file the picked (or cropped) file.
 * @returns `'type'`, `'empty'`, `'size'`, or `null` when it may be sent.
 */
export function logoFileProblem(file: {
  type: string;
  size: number;
}): 'type' | 'size' | 'empty' | null {
  if (!(LOGO_CONTENT_TYPES as readonly string[]).includes(file.type)) return 'type';
  if (file.size <= 0) return 'empty';
  if (file.size > LOGO_MAX_BYTES) return 'size';
  return null;
}

/**
 * The company's logo as a fifteen-minute URL. Only call it when the employer
 * says `hasLogo` — a company without one answers 404.
 * @param employerId the company's full id.
 * @returns the logo.
 * @throws when `employerId` is empty, or on an HTTP failure (404 = no logo).
 */
export async function getLogo(employerId: string): Promise<EmployerLogo> {
  if (!employerId) throw new Error('employerId is required');
  const { data } = await httpClient.get<EmployerLogoWire>(
    `/employment/v1/employers/${seg(employerId)}/logo`,
    INLINE_ERRORS,
  );
  return normalizeLogo(data);
}

/** The direct-to-storage PUT was refused (S3 sends no problem detail). */
export class LogoStorageError extends Error {
  /** @param status the storage response status. */
  constructor(readonly status: number) {
    super(`Logo upload to storage failed (${status})`);
    this.name = 'LogoStorageError';
  }
}

/**
 * Upload a logo in the contract's three steps:
 *   1. `POST …/logo/upload-url` with the type and size → a presigned PUT;
 *   2. `PUT` the bytes straight to storage with exactly `UploadContentType`
 *      (plain `fetch`, no Authorization header — the signature is the URL);
 *   3. `PUT …/logo` to adopt it (the server checks the magic bytes and size).
 * @param employerId the company's full id.
 * @param file the cropped image.
 * @param fileName the file name to declare (its extension must agree with the type).
 * @returns the adopted logo.
 * @throws on any refusal (steps 1 and 3 carry an RFC 9457 `detail`), or a
 *   `LogoStorageError` when storage refuses the PUT.
 */
export async function uploadLogo(
  employerId: string,
  file: Blob,
  fileName?: string,
): Promise<EmployerLogo> {
  if (!employerId) throw new Error('employerId is required');
  const { data: slot } = await httpClient.post<{
    UploadUrl?: string;
    UploadContentType?: string;
  }>(
    `/employment/v1/employers/${seg(employerId)}/logo/upload-url`,
    { ContentType: file.type, Size: file.size, ...(fileName ? { FileName: fileName } : {}) },
    INLINE_ERRORS,
  );
  if (!slot?.UploadUrl) throw new LogoStorageError(0);
  const put = await fetch(slot.UploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': slot.UploadContentType ?? file.type },
    body: file,
  });
  if (!put.ok) throw new LogoStorageError(put.status);
  const { data } = await httpClient.put<EmployerLogoWire>(
    `/employment/v1/employers/${seg(employerId)}/logo`,
    undefined,
    INLINE_ERRORS,
  );
  return normalizeLogo(data);
}

/**
 * Remove the company's logo (idempotent: removing a missing logo is a 204).
 * @param employerId the company's full id.
 * @throws when `employerId` is empty, or on an HTTP failure.
 */
export async function deleteLogo(employerId: string): Promise<void> {
  if (!employerId) throw new Error('employerId is required');
  await httpClient.delete(`/employment/v1/employers/${seg(employerId)}/logo`, INLINE_ERRORS);
}

/**
 * The file name to declare for a cropped logo, with the extension matching
 * its type (the server refuses a mismatch).
 * @param original the picked file's name.
 * @param type the cropped blob's type.
 * @returns e.g. `acme.png`.
 */
export function logoFileName(original: string, type: string): string {
  const ext = type === 'image/jpeg' ? 'jpg' : type === 'image/webp' ? 'webp' : 'png';
  const base = original.replace(/\.[^./\\]*$/, '').replace(/[^\w.-]+/g, '-') || 'logo';
  return `${base.slice(0, 120)}.${ext}`;
}

// ---------------------------------------------------------------------------
// Company tree helpers (parent Select, purge gating)
// ---------------------------------------------------------------------------

/**
 * Every employer under `employerId` (children, grandchildren, …), walked via
 * `parentId`. Cycle-safe.
 * @param employers the tenant's employers.
 * @param employerId the root of the walk.
 * @returns the descendants' ids (the root excluded).
 */
export function descendantIds(
  employers: readonly Pick<Employer, 'id' | 'parentId'>[],
  employerId: string,
): Set<string> {
  const childrenOf = new Map<string, string[]>();
  for (const e of employers) {
    if (!e.parentId) continue;
    const list = childrenOf.get(e.parentId) ?? [];
    list.push(e.id);
    childrenOf.set(e.parentId, list);
  }
  const out = new Set<string>();
  const queue = [employerId];
  while (queue.length > 0) {
    const current = queue.shift() as string;
    for (const child of childrenOf.get(current) ?? []) {
      if (child === employerId || out.has(child)) continue;
      out.add(child);
      queue.push(child);
    }
  }
  return out;
}

/**
 * The companies `employerId` may sit under (V1 `computeParentOptions`): every
 * Kind COMPANY employer (the tenant root included) except itself and its
 * descendants. Outsourcing clients, vendors and projects are never parents.
 * Sorted by full name. UX only — the server re-checks every rule.
 * @param employers the tenant's employers.
 * @param employerId the company being edited, or `null` on create.
 * @returns the eligible parents.
 */
export function parentCandidates(
  employers: readonly Employer[],
  employerId: string | null,
): Employer[] {
  const excluded = employerId ? descendantIds(employers, employerId) : new Set<string>();
  if (employerId) excluded.add(employerId);
  return employers
    .filter((e) => e.kind === 'COMPANY' && !excluded.has(e.id))
    .sort((a, b) => employerFullName(a).localeCompare(employerFullName(b)));
}

/** The V1 parent-rule codes the server answers in `errors[].code`. */
export const PARENT_ERROR_CODES = [
  'SELF_PARENT',
  'PARENT_NOT_FOUND',
  'PARENT_KIND_INVALID',
  'TENANT_ROOT_IMMUTABLE',
  'CYCLE_DETECTED',
  'DEPTH_EXCEEDED',
] as const;

/**
 * The first `errors[].code` of a refused write, when the server sent one.
 * @param err whatever the write threw.
 * @returns the code, or `undefined`.
 */
export function problemCode(err: unknown): string | undefined {
  const data = (err as { response?: { data?: unknown } } | null | undefined)?.response?.data;
  if (!data || typeof data !== 'object') return undefined;
  const errors = (data as { errors?: unknown }).errors;
  if (!Array.isArray(errors)) return undefined;
  for (const e of errors) {
    const code = (e as { code?: unknown } | null)?.code;
    if (typeof code === 'string' && code.trim() !== '') return code.trim();
  }
  return undefined;
}

/**
 * The human reason out of a refused call: the RFC 9457 `detail` (else
 * `title`), or a legacy `{message}` body. `undefined` when the server sent
 * nothing usable, so the caller falls back to its own translated sentence.
 * @param err whatever the call threw.
 * @returns the server's reason, trimmed, or `undefined`.
 */
export function lifecycleServerReason(err: unknown): string | undefined {
  const data = (err as { response?: { data?: unknown } } | null | undefined)?.response?.data;
  if (typeof data === 'string') return data.trim() || undefined;
  if (!data || typeof data !== 'object') return undefined;
  const body = data as Record<string, unknown>;
  for (const key of ['detail', 'Message', 'message', 'title']) {
    const v = body[key];
    if (typeof v === 'string' && v.trim() !== '') return v.trim();
  }
  return undefined;
}

/**
 * The field complaints of a refused write (`errors[].message`, prefixed with
 * the field), de-duplicated.
 * @param err whatever the write threw.
 * @returns e.g. `['Notes: Too big: expected string to have <=2000 characters']`.
 */
export function problemFieldMessages(err: unknown): string[] {
  const data = (err as { response?: { data?: unknown } } | null | undefined)?.response?.data;
  if (!data || typeof data !== 'object') return [];
  const errors = (data as { errors?: unknown }).errors;
  if (!Array.isArray(errors)) return [];
  const out = new Set<string>();
  for (const e of errors) {
    const message = (e as { message?: unknown } | null)?.message;
    if (typeof message !== 'string' || message.trim() === '') continue;
    const field = (e as { field?: unknown }).field;
    out.add(
      typeof field === 'string' && field.trim()
        ? `${field.trim()}: ${message.trim()}`
        : message.trim(),
    );
  }
  return [...out];
}

/**
 * The sentence to show for a refused write. A 400 validation problem's
 * `detail` is generic ("The request failed validation."), so its field
 * complaints are appended — the user sees WHICH field and why. Other refusals
 * (403 / 409 with a specific `detail`) show the `detail` alone.
 * @param err whatever the write threw.
 * @returns the text, or `undefined` when the server sent nothing usable.
 */
export function refusalText(err: unknown): string | undefined {
  const reason = lifecycleServerReason(err);
  if (lifecycleErrorStatus(err) !== 400) return reason;
  const fields = problemFieldMessages(err);
  if (fields.length === 0) return reason;
  return reason ? `${reason} ${fields.join('; ')}` : fields.join('; ');
}

/**
 * The HTTP status of a failed call, when there is one.
 * @param err whatever the call threw.
 * @returns the status code, or `undefined`.
 */
export function lifecycleErrorStatus(err: unknown): number | undefined {
  const status = (err as { response?: { status?: unknown } } | null | undefined)?.response?.status;
  return typeof status === 'number' ? status : undefined;
}
